import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import MoviePDP from './MoviePDP';
import SeriesWatchPage from './SeriesWatchPage';
import spotlightService from '../services/SpotlightService';
import './ContentRouter.css';

/**
 * ContentRouter Component
 *
 * Smart router that detects content type (movie vs series) and routes to appropriate component:
 * - Movies: MoviePDP with isWatchMode={true}
 * - Series: SeriesWatchPage with season/episode selection
 *
 * Handles both direct URL access and navigation from modals/components
 */
const ContentRouter = React.memo(() => {
  const { channelSlug, episodeId: pathEpisodeId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Read episodeId from path (/watch/:channelSlug/:episodeId — written by SeriesPDP)
  // and fall back to ?_id= query (written by SeriesWatchPage when the in-watch
  // dropdown changes episode). Path wins when both are present.
  const episodeId = useMemo(
    () => pathEpisodeId || searchParams.get('_id'),
    [pathEpisodeId, searchParams],
  );

  const [contentType, setContentType] = useState(null);
  const [contentData, setContentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Memoize contentData to prevent unnecessary re-renders
  const memoizedContentData = useMemo(() => contentData, [contentData?._id]); // Only depend on stable ID

  const detectContentType = useCallback(async () => {
    // Early exit to prevent infinite loop - only exit if no channelSlug
    if (!channelSlug) return;

    try {
      setLoading(true);
      setError(null);

      // Use getChannelBySlug - much simpler!
      const channelData = await spotlightService.getChannelBySlug(channelSlug);

      if (!channelData) {
        throw new Error('Channel not found');
      }

      // Determine content type based on channel data
      let detectedType = 'movie'; // Default to movie

      // Handle different API response structures
      const channel = channelData.channels?.[0] || channelData;

      if (channel) {
        // Check for series indicators
        if (
          channel.programming_type === 'Series' ||
          channel.channel_type === 'parent' ||
          (channel.childchannels && channel.childchannels.length > 0) ||
          (channel.child_channels && channel.child_channels.length > 0)
        ) {
          detectedType = 'series';
        }
      }

      setContentType(detectedType);
      setContentData(channel); // Pass the actual channel object, not the wrapper
    } catch (error) {
      console.error('ContentRouter: Error loading channel:', error);
      setError(error.message || 'Failed to load channel');
    } finally {
      setLoading(false);
    }
  }, [channelSlug]);

  useEffect(() => {
    detectContentType();
  }, [detectContentType]);

  // Memoize the SeriesWatchPage props to prevent unnecessary remounts
  const seriesWatchPageProps = useMemo(
    () => ({
      channelSlug,
      seriesData: memoizedContentData,
      initialEpisodeId: episodeId,
    }),
    [channelSlug, memoizedContentData?._id, episodeId], // Only depend on stable IDs
  );

  // Memoize the MoviePDP props to prevent unnecessary remounts
  const moviePDPProps = useMemo(
    () => ({
      movieId: channelSlug,
      isWatchMode: true,
    }),
    [channelSlug],
  );

  // Loading state
  if (loading) {
    return (
      <div className="content-router-loading">
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
          <p>Loading content...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="content-router-error">
        <div className="error-container">
          <h2>Content Not Found</h2>
          <p>Failed to load content: {error}</p>
          <div className="error-actions">
            <button onClick={() => navigate(-1)} className="btn-secondary">
              Go Back
            </button>
            <button onClick={() => window.location.reload()} className="btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Route to appropriate component based on content type
  if (contentType === 'series') {
    return (
      <SeriesWatchPage
        key={`series-${channelSlug}-${contentData?._id || 'loading'}`} // Stable key based on content ID
        {...seriesWatchPageProps}
      />
    );
  } else {
    return (
      <MoviePDP
        key={`movie-${channelSlug}-${contentData?._id || 'loading'}`} // Stable key based on content ID
        {...moviePDPProps}
      />
    );
  }
});

export default ContentRouter;
